/*
 * Delivery rules for catering orders, shared by the Catering Cart Summary section and
 * the cart drawer. One implementation on purpose: when the two routes had separate code,
 * only one of them collected the delivery details and orders arrived unfulfillable.
 *
 * A "scope" is any element containing both a set of catering fields and a submit button,
 * marked with data-catering-scope. The drawer's footer sits outside its <form>, so state
 * is read from the fields directly and written through the cart AJAX API rather than
 * relying on form submission.
 */
(function () {
  const LEAD_TIME_HOURS = 48;
  const SHOP_TIMEZONE = 'Asia/Hong_Kong';

  // Bookable window
  const START_HOUR = 7;
  const START_MIN = 30;
  const END_HOUR = 18;
  const END_MIN = 0;

  const pad = (n) => String(n).padStart(2, '0');
  const formatTime24 = (h, m) => `${pad(h)}:${pad(m)}`;

  function formatTimeAMPM(h, m) {
    const hour = h % 12 || 12;
    return `${hour}:${pad(m)} ${h < 12 ? 'AM' : 'PM'}`;
  }

  // Wall-clock "now" in the shop's timezone, so the lead time is identical for every
  // customer no matter how their own device clock is set.
  function shopNowUTC() {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: SHOP_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date());
    const get = (type) => Number(parts.find((p) => p.type === type).value);
    return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
  }

  const asDate = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

  // Earliest bookable slot. Recomputed on demand, never cached at load, so a tab left
  // open overnight cannot drift into an earlier cutoff.
  function earliest() {
    const c = new Date(shopNowUTC() + LEAD_TIME_HOURS * 60 * 60 * 1000);
    const date = asDate(c);
    const time = formatTime24(c.getUTCHours(), c.getUTCMinutes());

    // Ordering in the evening pushes the cutoff past the last slot of that day, which
    // would leave the earliest date with nothing bookable on it. Roll to the next day,
    // where every slot clears the lead time.
    if (time > formatTime24(END_HOUR, END_MIN)) {
      return { date: asDate(new Date(c.getTime() + 24 * 60 * 60 * 1000)), time: '00:00' };
    }

    return { date: date, time: time };
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => {
      if (!r.ok) throw new Error(url + ' failed');
      return r.json();
    });
  }

  function init(scope) {
    const dateInput = scope.querySelector('[data-catering-date]');
    const timeSelect = scope.querySelector('[data-catering-time]');
    const agreement = scope.querySelector('[data-catering-agreement]');
    const submit = scope.querySelector('[data-catering-submit]');
    if (!dateInput || !timeSelect || !agreement || !submit) return;

    const disposables = scope.querySelector('[data-catering-disposables]');
    const note = scope.querySelector('[data-catering-note]');
    const methods = scope.querySelectorAll('[data-catering-method]');
    const pickupInput = scope.querySelector('[data-catering-pickup-variant]');
    const errorBox = scope.querySelector('[data-catering-error]');
    const pickupVariantId = pickupInput ? Number(pickupInput.value) : null;

    const savedTime = timeSelect.dataset.savedTime || '';

    function pickupSelected() {
      const chosen = [...methods].find((m) => m.checked);
      return chosen ? chosen.value !== 'Delivery' : false;
    }

    function buildTimes() {
      const min = earliest();
      const previous = timeSelect.value || savedTime;

      timeSelect.innerHTML = '';

      for (let h = START_HOUR; h <= END_HOUR; h++) {
        for (let m = 0; m < 60; m += 30) {
          if (h === START_HOUR && m < START_MIN) continue;
          if (h === END_HOUR && m > END_MIN) continue;

          const time24 = formatTime24(h, m);
          const endHour = m + 30 >= 60 ? h + 1 : h;
          const endMin = (m + 30) % 60;

          const option = document.createElement('option');
          // The whole slot is submitted, not just its start, so the order and the
          // confirmation email read "9:30 AM - 10:00 AM" rather than "9:30 AM".
          option.value = `${formatTimeAMPM(h, m)} - ${formatTimeAMPM(endHour, endMin)}`;
          option.textContent = option.value;
          option.dataset.time24 = time24;
          if (dateInput.value === min.date && time24 < min.time) option.disabled = true;

          timeSelect.appendChild(option);
        }
      }

      const options = [...timeSelect.options];
      const keep = options.find((o) => o.value === previous && !o.disabled);
      const firstEnabled = options.find((o) => !o.disabled);
      timeSelect.value = keep ? keep.value : firstEnabled ? firstEnabled.value : '';
    }

    function applyMinDate() {
      const min = earliest();
      dateInput.min = min.date;
      if (!dateInput.value || dateInput.value < min.date) dateInput.value = min.date;
      buildTimes();
    }

    // Single source of truth. The date input's `min` is only a hint to the picker, so
    // every rule is re-checked here immediately before the order is sent.
    function validationError() {
      const min = earliest();

      if (!dateInput.value) return 'Please choose a delivery date.';
      if (dateInput.value < min.date) {
        return `We need at least ${LEAD_TIME_HOURS} hours to prepare your order. The earliest date we can deliver is ${min.date}.`;
      }

      const option = timeSelect.selectedOptions[0];
      if (!timeSelect.value || !option) return 'Please select a delivery time.';
      if (option.disabled || (dateInput.value === min.date && option.dataset.time24 < min.time)) {
        return `We need at least ${LEAD_TIME_HOURS} hours to prepare your order. Please choose a later time slot.`;
      }

      if (!agreement.checked) return 'Please confirm you have read the catering policy.';

      return null;
    }

    function showError(message) {
      if (!errorBox) {
        if (message) alert(message);
        return;
      }
      errorBox.textContent = message || '';
      errorBox.hidden = !message;
    }

    function attributes() {
      const chosen = [...methods].find((m) => m.checked);
      return {
        'Delivery Method': chosen ? chosen.value : 'Delivery',
        Date: dateInput.value,
        Time: timeSelect.value,
        'Delivery Agreement': agreement.checked ? 'Agreed' : '',
        'Disposable Items Required': disposables && disposables.checked ? 'Yes' : 'No',
      };
    }

    function saveAttributes() {
      const body = { attributes: attributes() };
      if (note) body.note = note.value;
      return postJSON('/cart/update.js', body);
    }

    // Persist as the customer types so the details survive the drawer re-rendering when
    // items change, and carry over between the drawer and the catering page.
    let saveTimer = null;
    function queueSave() {
      clearTimeout(saveTimer);
      saveTimer = setTimeout(() => saveAttributes().catch(() => {}), 500);
    }

    applyMinDate();
    showError(null);

    // The theme hides the native picker indicator (appearance:none), so the calendar
    // icon is only a background image with nothing clickable behind it. Typing is
    // blocked too, which would leave no way to set a date at all -- open it explicitly.
    dateInput.addEventListener('click', function () {
      if (typeof dateInput.showPicker !== 'function') return;
      try {
        dateInput.showPicker();
      } catch (error) {
        /* Browsers reject showPicker outside a user gesture; the field still works. */
      }
    });

    dateInput.addEventListener('change', function () {
      // A new date invalidates the agreement, which must be given for the date booked
      agreement.checked = false;
      applyMinDate();
      showError(null);
      queueSave();
    });

    [timeSelect, agreement, disposables, note, ...methods].forEach((el) => {
      if (!el) return;
      el.addEventListener('change', function () {
        showError(null);
        queueSave();
      });
    });

    // A page restored from the back/forward cache carries a stale cutoff
    window.addEventListener('pageshow', applyMinDate);

    submit.addEventListener('click', async function (event) {
      event.preventDefault();

      const problem = validationError();
      if (problem) {
        showError(problem);
        return;
      }

      const originalText = submit.textContent;
      submit.disabled = true;

      try {
        await saveAttributes();

        const cart = await fetch('/cart.js').then((r) => r.json());
        const pickupLine = cart.items.find((i) => Number(i.variant_id) === pickupVariantId);

        if (pickupSelected() && !pickupLine && pickupVariantId) {
          await postJSON('/cart/add.js', { items: [{ id: pickupVariantId, quantity: 1 }] });
        }

        if (!pickupSelected() && pickupLine) {
          await postJSON('/cart/update.js', { updates: { [pickupLine.key]: 0 } });
        }

        window.location.href = '/checkout';
      } catch (error) {
        console.error(error);
        submit.disabled = false;
        submit.textContent = originalText;
        showError('Something went wrong saving your delivery details. Please try again.');
      }
    });
  }

  function initAll() {
    document.querySelectorAll('[data-catering-scope]').forEach(function (scope) {
      if (scope.dataset.cateringReady) return;
      scope.dataset.cateringReady = '1';
      init(scope);
    });
  }

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  ready(function () {
    initAll();

    // The drawer replaces its own markup whenever the cart changes, which drops the
    // wiring above along with it. Re-initialise whatever comes back.
    const drawer = document.querySelector('cart-drawer');
    if (drawer) new MutationObserver(initAll).observe(drawer, { childList: true, subtree: true });
  });
})();
